import { useEffect, useMemo, useState, useCallback } from 'react';
import { PartTypeFilterIds } from 'views/parts/PartsV2/Parts/constants';
import {
  ALL,
  DISABLE_PAGE_LIMIT,
  QueryFilterOperators,
  UUID,
  QueryParams,
} from 'types/api';
import {
  OrgPartSchema,
  OrgPartSchemaLite,
  PublicPartSchema,
  PublicPartSchemaSearch,
} from 'types/clientV2/parts';
import {
  useGetOrgPartsQuery,
  useLazySearchAllPartsQuery,
  usePrefetch,
  useSearchPublicPartsQuery,
} from 'store/slices/clientV2/parts';
import _ from 'lodash';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import useRequestDecoratedPartData from 'hooks/useRequestDecoratedPartData';
import { PartRequestStateType, PartSchemas } from 'types/partStatus';
import { PartSupply } from 'types/part';
import { handleErr } from 'utils/functions';
import useServerSideDatagrid from 'hooks/useServerSideDatagrid';
import { Org } from 'types/org';

export type UsePartsV2SearchReturn = {
  selectedPartType: PartTypeFilterIds;
  setSelectedPartType: (partType: PartTypeFilterIds) => void;
  orgParts: {
    foundOrgParts: PartSearchSchemaType[];
    isLoading: boolean;
    rowCount: number | undefined;
    setters: Omit<
      ReturnType<
        typeof useServerSideDatagrid<OrgPartSchema, OrgPartSchemaLite>
      >['setters'],
      'setSearchSchema'
    >;
    queryParams: QueryParams<OrgPartSchema>;
  };
  publicParts: {
    foundPublicParts: PartSearchSchemaType[];
    isLoading: boolean;
    isError: boolean;
  };
  bothParts: {
    parts: PartSearchSchemaType[];
    isLoading: boolean;
    isError: boolean;
  };
  searchString: string;
  handleSetSearchString: (searchVal: string) => void;
  handleImmediateSearch: (searchVal: string) => void;
  supply: {
    supplyData: Record<string, Record<PartSchemas, PartSupply>>;
    supplyStatus: _.Dictionary<PartRequestStateType>;
  };
  debouncedUrlSearch: string;
};

export type PartSearchSchemaType = {
  id: UUID;
  part: PublicPartSchemaSearch | OrgPartSchemaLite | OrgPartSchema;
  orgPartId?: OrgPartSchemaLite['id'];
  source: PartTypeFilterIds;
  partDataType: PartTypeFilterIds.ORG | PartTypeFilterIds.PUBLIC;
  supply?: PartSupply;
  defaultSelectedPartType?: PartTypeFilterIds;
};

type UsePartsV2SearchProps = {
  requireSearchStringForResults?: boolean;
  overrideOrgId?: Org['id'];
  defaultSelectedPartType?: PartTypeFilterIds;
  expandPublicPartSchema?: boolean;
};

export default function usePartsV2Search({
  requireSearchStringForResults = false,
  overrideOrgId,
  defaultSelectedPartType,
  expandPublicPartSchema = false,
}: UsePartsV2SearchProps): UsePartsV2SearchReturn {
  const [selectedPartType, setSelectedPartType] = useState<PartTypeFilterIds>(
    defaultSelectedPartType || PartTypeFilterIds.BOTH
  );
  const [searchString, setSearchString] = useState<string>('');
  const [debouncedUrlSearch, setDebouncedUrlSearch] = useState<string>('');
  const [debouncedOrgSearch, setDebouncedOrgSearch] = useState<string>('');
  const [debouncedPublicSearch, setDebouncedPublicSearch] =
    useState<string>('');
  const [isDebouncingPublicSearch, setIsDebouncingPublicSearch] =
    useState<boolean>(false);
  const [isDebouncingOrgSearch, setIsDebouncingOrgSearch] =
    useState<boolean>(false);
  const [
    hasCompletedPublicPartSearchAfterEmpty,
    setHasCompletedPublicPartSearchAfterEmpty,
  ] = useState<boolean>(true);
  const [
    hasCompletedBothPartSearchAfterEmpty,
    setHasCompletedBothPartSearchAfterEmpty,
  ] = useState<boolean>(true);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const [
    searchAllParts,
    {
      data: allParts,
      isFetching: isFetchingAllParts,
      isError: isErrorAllParts,
      isSuccess: isSuccessAllParts,
    },
  ] = useLazySearchAllPartsQuery();

  useEffect(() => {
    if (isSuccessAllParts || (isErrorAllParts && !isFetchingAllParts)) {
      setHasCompletedBothPartSearchAfterEmpty(true);
    }
  }, [
    isSuccessAllParts,
    setHasCompletedBothPartSearchAfterEmpty,
    isErrorAllParts,
    isFetchingAllParts,
  ]);

  const handleDebounceOrgSearch = useMemo(
    () =>
      _.debounce((searchValue: string) => {
        setDebouncedOrgSearch(searchValue.trim());
        setIsDebouncingOrgSearch(false);
      }, 100),
    [setDebouncedOrgSearch, setIsDebouncingOrgSearch]
  );

  const handleDebounceUrlSearch = useMemo(
    () =>
      _.debounce((searchValue: string) => {
        setDebouncedUrlSearch(searchValue.trim());
      }, 500),
    [setDebouncedUrlSearch]
  );

  const handleDebouncedPublicSearchShortString = useMemo(
    () =>
      _.debounce((searchValue: string) => {
        setDebouncedPublicSearch(searchValue.trim());
        setIsDebouncingPublicSearch(false);
      }, 5000),
    [setDebouncedPublicSearch, setIsDebouncingPublicSearch]
  );

  const handleDebouncedPublicSearchLongString = useMemo(
    () =>
      _.debounce((searchValue: string) => {
        setDebouncedPublicSearch(searchValue.trim());
        setIsDebouncingPublicSearch(false);
      }, 1000),
    [setDebouncedPublicSearch, setIsDebouncingPublicSearch]
  );

  useEffect(() => {
    const trimmedSearch = searchString.trim();

    if (trimmedSearch.startsWith('http')) {
      handleDebounceUrlSearch(trimmedSearch);
    } else if (trimmedSearch.length >= 5) {
      handleDebounceOrgSearch(trimmedSearch);
      setIsDebouncingPublicSearch(true);
      setDebouncedUrlSearch('');
      handleDebouncedPublicSearchShortString.cancel();
      handleDebouncedPublicSearchLongString(trimmedSearch);
    } else if (trimmedSearch.length > 0) {
      handleDebounceOrgSearch(trimmedSearch);
      setIsDebouncingPublicSearch(true);
      setDebouncedUrlSearch('');
      handleDebouncedPublicSearchShortString(trimmedSearch);
    } else {
      handleDebounceOrgSearch('');
      setDebouncedPublicSearch('');
      setIsDebouncingPublicSearch(false);
      setDebouncedUrlSearch('');
      setHasCompletedPublicPartSearchAfterEmpty(false);
      setHasCompletedBothPartSearchAfterEmpty(false);
    }

    return () => {
      handleDebounceOrgSearch.cancel();
      handleDebouncedPublicSearchShortString.cancel();
      handleDebouncedPublicSearchLongString.cancel();
    };
  }, [
    searchString,
    handleDebounceOrgSearch,
    handleDebouncedPublicSearchShortString,
    handleDebouncedPublicSearchLongString,
  ]);

  const shouldSearchPublicParts = useMemo(
    () =>
      !!debouncedPublicSearch.trim() &&
      selectedPartType === PartTypeFilterIds.PUBLIC,
    [debouncedPublicSearch, selectedPartType]
  );

  const shouldSearchOrgParts = useMemo(
    () =>
      selectedPartType === PartTypeFilterIds.ORG &&
      (!requireSearchStringForResults || !!debouncedOrgSearch.trim()),
    [debouncedOrgSearch, selectedPartType, requireSearchStringForResults]
  );

  const {
    data: searchedPublicParts,
    isFetching: isFetchingSearchedPublicParts,
    isError: isErrorSearchedPublicParts,
    isSuccess: isSuccessSearchedPublicParts,
    refetch: refetchPublicParts,
  } = useSearchPublicPartsQuery(
    shouldSearchPublicParts
      ? {
          search: debouncedPublicSearch,
          external: true,
          ...(overrideOrgId ? { orgId: overrideOrgId } : {}),
        }
      : skipToken
  );

  useEffect(() => {
    if (
      isSuccessSearchedPublicParts ||
      (isErrorSearchedPublicParts && !isFetchingSearchedPublicParts)
    ) {
      setHasCompletedPublicPartSearchAfterEmpty(true);
    }
  }, [
    isSuccessSearchedPublicParts,
    setHasCompletedPublicPartSearchAfterEmpty,
    isErrorSearchedPublicParts,
    isFetchingSearchedPublicParts,
  ]);

  const schema = useMemo(() => {
    if (expandPublicPartSchema) {
      return [ALL, { publicPart: ['id', 'deprecatedBy'] }] as any;
    }
    return [ALL];
  }, [expandPublicPartSchema]);

  const {
    data: foundOrgParts,
    isFetching: isFetchingOrgParts,
    setters: { setSearchSchema: setOrgPartSearchSchema, ...orgPartSetters },
    queryParams: orgPartQueryParams,
  } = useServerSideDatagrid<OrgPartSchema, OrgPartSchemaLite>({
    useResourceHook: useGetOrgPartsQuery,
    usePrefetch,
    prefetchName: 'getOrgParts',
    params: {
      schema,
      filters: [],
      searchSchema: [ALL],
      search: debouncedOrgSearch,
      ...(overrideOrgId ? { overrideOrgId } : {}),
    },
    skip: !shouldSearchOrgParts,
  });

  const getTopParts = useCallback(async () => {
    try {
      await searchAllParts({
        search: debouncedPublicSearch,
        external: true,
        ...(overrideOrgId ? { orgId: overrideOrgId } : {}),
      }).unwrap();
    } catch (err) {
      handleErr(err, (_errMessage: string) => {});
    }
  }, [searchAllParts, debouncedPublicSearch, overrideOrgId]);

  useEffect(() => {
    if (debouncedPublicSearch && selectedPartType === PartTypeFilterIds.BOTH) {
      getTopParts().catch(() => {
        // Silently catch the error to prevent "Uncaught (in promise)" console errors
        // The error is already handled by handleErr and the RTK Query hook will set isError
      });
    }
  }, [debouncedPublicSearch, selectedPartType, getTopParts]);

  useEffect(
    () => () => {
      handleDebounceOrgSearch.cancel();
      handleDebouncedPublicSearchShortString.cancel();
      handleDebouncedPublicSearchLongString.cancel();
    },
    [
      handleDebounceOrgSearch,
      handleDebouncedPublicSearchShortString,
      handleDebouncedPublicSearchLongString,
    ]
  );

  const orgPartsForPublicPartsIds = useMemo(
    () => [
      ..._.map(searchedPublicParts?.data, 'id'),
      ..._.map(allParts?.publicParts, 'id'),
    ],
    [searchedPublicParts?.data, allParts?.publicParts]
  );

  const {
    data: orgPartsForPublicParts,
    isFetching: isFetchingOrgPartsForPublicParts,
  } = useServerSideQuery<
    OrgPartSchema,
    Pick<OrgPartSchemaLite, 'id' | 'publicPart'>
  >(
    useGetOrgPartsQuery,
    shouldSearchPublicParts && orgPartsForPublicPartsIds?.length
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          schema: ['id', 'publicPart'],
          filters: [
            {
              field: 'publicPart.id',
              operator: QueryFilterOperators.isAnyOf,
              value: orgPartsForPublicPartsIds,
            },
          ],
          ...(overrideOrgId ? { overrideOrgId } : {}),
        }
      : skipToken
  );

  // Determine which DecoratedPartData we need to fetch
  // This is based on CPIDs, and allows us to render supply data
  const cpidsToFetch: string[] = useMemo(
    () =>
      _.chain([
        ...(searchedPublicParts?.data ?? []),
        ...(allParts?.orgParts ?? []),
        ...(allParts?.publicParts ?? []),
        ...(foundOrgParts?.data ?? []),
      ])
        .map((part) => part.cofactrId)
        .compact()
        .uniq()
        .value(),
    [
      searchedPublicParts?.data,
      allParts?.orgParts,
      allParts?.publicParts,
      foundOrgParts?.data,
    ]
  );

  const { data: supplyData, status: supplyStatus } =
    useRequestDecoratedPartData({
      partIds: cpidsToFetch,
      schemas: [PartSchemas.supply],
      cacheKey: `parSearchData`,
    });

  const orgParts: PartSearchSchemaType[] = useMemo(() => {
    if (requireSearchStringForResults && !searchString.trim()) {
      return [];
    }

    return _.map(foundOrgParts?.data ?? [], (part) => ({
      id: part.id,
      part,
      source: part?.publicPart ? PartTypeFilterIds.BOTH : PartTypeFilterIds.ORG,
      partDataType: PartTypeFilterIds.ORG,
      supply:
        part?.cofactrId && supplyData[part.cofactrId]
          ? supplyData[part.cofactrId][PartSchemas.supply]
          : undefined,
    }));
  }, [
    foundOrgParts?.data,
    searchString,
    supplyData,
    requireSearchStringForResults,
  ]);

  const foundOrgPartsForPublicPartsIds = useMemo(
    () => _.map(orgPartsForPublicParts?.data, (part) => part?.publicPart),
    [orgPartsForPublicParts?.data]
  );

  const publicPartsData: PartSearchSchemaType[] = useMemo(() => {
    if (
      !searchString.trim() ||
      !hasCompletedPublicPartSearchAfterEmpty ||
      isErrorSearchedPublicParts
    ) {
      return [];
    }

    return _.map(searchedPublicParts?.data ?? [], (part) => ({
      id: part.id,
      part,
      source: foundOrgPartsForPublicPartsIds.includes(part.id)
        ? PartTypeFilterIds.BOTH
        : PartTypeFilterIds.PUBLIC,
      partDataType: PartTypeFilterIds.PUBLIC,
      orgPartId:
        _.find(
          orgPartsForPublicParts?.data,
          (orgPart) => orgPart?.publicPart === part.id
        )?.id ?? undefined,
      supply:
        part?.cofactrId && supplyData[part.cofactrId]
          ? supplyData[part.cofactrId][PartSchemas.supply]
          : undefined,
    }));
  }, [
    searchedPublicParts?.data,
    foundOrgPartsForPublicPartsIds,
    searchString,
    supplyData,
    orgPartsForPublicParts?.data,
    hasCompletedPublicPartSearchAfterEmpty,
    isErrorSearchedPublicParts,
  ]);

  const getBothPublicParts = useCallback(
    (bothPublicParts: PublicPartSchema[]) =>
      _.chain(bothPublicParts)
        .map(
          (part) =>
            ({
              id: part.id,
              part,
              source: foundOrgPartsForPublicPartsIds.includes(part.id)
                ? PartTypeFilterIds.BOTH
                : PartTypeFilterIds.PUBLIC,
              partDataType: PartTypeFilterIds.PUBLIC,
              orgPartId: _.find(
                orgPartsForPublicParts?.data,
                (orgPart) => orgPart?.publicPart === part.id
              )?.id,
              supply:
                part?.cofactrId && supplyData[part.cofactrId]
                  ? supplyData[part.cofactrId][PartSchemas.supply]
                  : undefined,
            } as PartSearchSchemaType)
        )
        .value(),
    [foundOrgPartsForPublicPartsIds, orgPartsForPublicParts?.data, supplyData]
  );

  const getBothOrgParts = useCallback(
    (bothOrgParts: OrgPartSchema[]) =>
      _.map(
        bothOrgParts,
        (part) =>
          ({
            id: part.id,
            part,
            source: part?.publicPart
              ? PartTypeFilterIds.BOTH
              : PartTypeFilterIds.ORG,
            partDataType: PartTypeFilterIds.ORG,
            supply:
              part?.cofactrId && supplyData[part.cofactrId]
                ? supplyData[part.cofactrId][PartSchemas.supply]
                : undefined,
          } as PartSearchSchemaType)
      ),
    [supplyData]
  );

  const bothPartsData: PartSearchSchemaType[] = useMemo(() => {
    if (
      !searchString.trim() ||
      !hasCompletedBothPartSearchAfterEmpty ||
      isErrorAllParts
    ) {
      return [];
    }

    const bothOrgParts = getBothOrgParts(allParts?.orgParts ?? []);
    const bothPublicParts = getBothPublicParts(allParts?.publicParts ?? []);
    return [...bothOrgParts, ...bothPublicParts];
  }, [
    allParts?.orgParts,
    allParts?.publicParts,
    searchString,
    getBothPublicParts,
    getBothOrgParts,
    hasCompletedBothPartSearchAfterEmpty,
    isErrorAllParts,
  ]);

  const handleSetSearchString = useCallback(
    (searchVal: string) => {
      setSearchString(searchVal);
    },
    [setSearchString]
  );

  const handleImmediateSearch = useCallback(
    async (searchVal: string) => {
      const trimmedSearch = searchVal.trim();
      setSearchString(searchVal);
      setDebouncedPublicSearch(trimmedSearch);
      setDebouncedOrgSearch(trimmedSearch);
      setIsDebouncingPublicSearch(false);

      if (
        (selectedPartType === PartTypeFilterIds.PUBLIC &&
          isErrorSearchedPublicParts) ||
        (selectedPartType === PartTypeFilterIds.BOTH && isErrorAllParts)
      ) {
        setShouldRefetch(true);
      }
    },
    [
      setSearchString,
      setDebouncedOrgSearch,
      setDebouncedPublicSearch,
      setIsDebouncingPublicSearch,
      setShouldRefetch,
      isErrorSearchedPublicParts,
      isErrorAllParts,
      selectedPartType,
    ]
  );

  useEffect(() => {
    if (shouldRefetch) {
      setShouldRefetch(false);
      const trimmedSearch = debouncedPublicSearch.trim();

      if (selectedPartType === PartTypeFilterIds.PUBLIC && trimmedSearch) {
        refetchPublicParts();
      } else if (selectedPartType === PartTypeFilterIds.BOTH && trimmedSearch) {
        searchAllParts({
          search: trimmedSearch,
          external: true,
          ...(overrideOrgId ? { orgId: overrideOrgId } : {}),
        });
      }
    }
  }, [
    shouldRefetch,
    debouncedPublicSearch,
    selectedPartType,
    refetchPublicParts,
    searchAllParts,
    overrideOrgId,
  ]);

  const orgPartsIsLoading = useMemo(
    () => isFetchingOrgParts || isDebouncingOrgSearch,
    [isFetchingOrgParts, isDebouncingOrgSearch]
  );

  const publicPartsIsLoading = useMemo(
    () =>
      isFetchingSearchedPublicParts ||
      isFetchingOrgPartsForPublicParts ||
      isDebouncingPublicSearch ||
      Boolean(searchString.trim() && !hasCompletedPublicPartSearchAfterEmpty),
    [
      isFetchingSearchedPublicParts,
      isFetchingOrgPartsForPublicParts,
      isDebouncingPublicSearch,
      hasCompletedPublicPartSearchAfterEmpty,
      searchString,
    ]
  );

  const bothPartsIsLoading = useMemo(
    () =>
      isFetchingAllParts ||
      isFetchingOrgPartsForPublicParts ||
      isDebouncingPublicSearch ||
      Boolean(searchString.trim() && !hasCompletedBothPartSearchAfterEmpty),
    [
      isFetchingAllParts,
      isFetchingOrgPartsForPublicParts,
      isDebouncingPublicSearch,
      hasCompletedBothPartSearchAfterEmpty,
      searchString,
    ]
  );

  return {
    selectedPartType,
    setSelectedPartType,
    orgParts: {
      foundOrgParts: orgParts,
      isLoading: orgPartsIsLoading,
      rowCount: foundOrgParts?.count,
      setters: orgPartSetters,
      queryParams: orgPartQueryParams,
    },
    publicParts: {
      foundPublicParts: publicPartsData,
      isLoading: publicPartsIsLoading,
      isError: isErrorSearchedPublicParts,
    },
    bothParts: {
      parts: bothPartsData,
      isLoading: bothPartsIsLoading,
      isError: isErrorAllParts,
    },
    searchString,
    handleSetSearchString,
    handleImmediateSearch,
    debouncedUrlSearch,
    supply: {
      supplyData,
      supplyStatus,
    },
  };
}
